import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { brandAssets, appStoreLinks } from '../../data/mock';
import { useAuth } from '../../context/AuthContext';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const menuRef = useRef(null);
  
  const isSessionsPage = location.pathname === '/sessions';

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/');
    window.location.reload();
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Close dropdown on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', name: 'Home', hoverColor: 'hover:text-purple-500' },
    { path: '/sessions', name: 'Sessions', hoverColor: 'hover:text-[#5E87FF]' },
    { path: '/playlists', name: 'Playlists', hoverColor: 'hover:text-[#7F56F5]' },
    { path: '/partners', name: 'Partners', hoverColor: 'hover:text-purple-500' },
    { path: '/pricing', name: 'Pricing', hoverColor: 'hover:text-purple-500' },
    { path: '/faq', name: 'FAQ', hoverColor: 'hover:text-purple-500' },
  ];

  const getActiveColor = (path) => {
    if (path === '/sessions') return 'text-[#5E87FF]';
    if (path === '/playlists') return 'text-[#7F56F5]';
    return 'text-purple-500';
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 transition-all duration-500 ${
        scrolled 
          ? 'bg-white/90 dark:bg-gray-900/90 backdrop-blur-2xl shadow-[0_2px_20px_rgba(0,0,0,0.06)]' 
          : 'bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl'
      }`}
      style={{ zIndex: 50 }}
      role="banner"
    >
      <nav className="max-w-6xl mx-auto px-4 sm:px-6" aria-label="Main navigation">
        <div className="flex items-center h-16">
          {/* Logo - Far Left */}
          <Link 
            to="/" 
            className="flex items-center flex-shrink-0 mr-8 relative" 
            aria-label="Home"
            data-testid="header-logo"
          >
            {isSessionsPage && (
              <>
                <div className="absolute inset-0 -m-2 rounded-full bg-[#5E87FF]/20 animate-ping hidden md:block" style={{ animationDuration: '2s' }} />
                <div className="absolute inset-0 -m-1 rounded-full bg-[#5E87FF]/10 animate-pulse hidden md:block" />
              </>
            )}
            <img 
              src={brandAssets.logo} 
              alt="Tap In" 
              className={`h-8 w-auto relative z-10 ${isSessionsPage ? 'animate-blue-glow rounded-full' : ''}`} 
            />
          </Link>

          {/* Desktop Nav - Center */}
          <div className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  location.pathname === link.path 
                    ? getActiveColor(link.path)
                    : `text-gray-900 dark:text-gray-100 ${link.hoverColor}`
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 ml-auto flex-shrink-0">
            {/* Sign In / Dashboard button */}
            {isAuthenticated ? (
              <Link 
                to="/app"
                className="gradient-btn-animated inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25 hover:scale-[1.02] whitespace-nowrap"
                data-testid="header-dashboard-btn"
              >
                Dashboard
              </Link>
            ) : (
              <Link 
                to="/auth"
                className="gradient-btn-animated inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25 hover:scale-[1.02]"
                data-testid="header-signin-btn"
              >
                Sign In
              </Link>
            )}

            {/* Hamburger Menu Button + Dropdown */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                data-testid="hamburger-menu-btn"
              >
                {menuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>

              {/* Dropdown Menu */}
              {menuOpen && (
                <div 
                  className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 py-3 z-50"
                  data-testid="dropdown-menu"
                >
                  {/* Nav links - always visible in dropdown */}
                  <div data-testid="dropdown-nav-links">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`block px-5 py-2.5 text-base font-medium text-center transition-colors ${
                          location.pathname === link.path 
                            ? `${getActiveColor(link.path)} bg-gray-50 dark:bg-gray-700`
                            : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                        onClick={() => setMenuOpen(false)}
                        data-testid={`dropdown-nav-${link.name.toLowerCase()}`}
                      >
                        {link.name}
                      </Link>
                    ))}
                    <div className="border-t border-gray-100 dark:border-gray-700 my-2" />
                  </div>

                  {/* Sign Out */}
                  {isAuthenticated && (
                    <button
                      onClick={handleLogout}
                      className="flex items-center justify-center gap-2 w-[calc(100%-24px)] mx-3 px-4 py-2.5 rounded-xl text-base font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors"
                      data-testid="dropdown-signout-btn"
                    >
                      <LogOut size={18} />
                      Sign Out
                    </button>
                  )}

                  {/* Download App */}
                  <a 
                    href={appStoreLinks.apple} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="download-btn-glow block mx-3 mt-2 text-center px-4 py-3 rounded-xl text-base font-semibold text-white cursor-pointer"
                    onClick={() => setMenuOpen(false)}
                    data-testid="dropdown-download-btn"
                  >
                    <span className="download-label-gradient">Download App</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
